/**
 * Fetches ContentStack data from the provided URL
 * @param {string} url - The ContentStack data URL
 * @returns {Promise<Response>} The fetch promise
 */
export const contentStackData = (url) =>
  fetch(url, {
    headers: {
      // eslint-disable-next-line camelcase
      api_key: String(process.env.REACT_APP_CONTENT_STACK_API_KEY),
      // eslint-disable-next-line camelcase
      access_token: String(process.env.REACT_APP_CONTENT_STACK_ACCESS_TOKEN),
    },
  });

/**
 * Converts an array of objects to CSV format
 * @param {Array} array - The array of objects to convert
 * @param {Array} columns - The column definitions for the CSV
 * @returns {string} The CSV formatted string
 */
export const convertArrayOfObjectsToCSV = (array, columns) => {
  let result;

  const columnDelimiter = ',';
  const lineDelimiter = '\n';

  const headerName = [];
  const headerSelector = [];

  for (const item of columns) {
    headerName.push(item.name);
    headerSelector.push(item.selector);
  }

  result = '';
  result += headerName.join(columnDelimiter);
  result += lineDelimiter;

  for (const item of array) {
    let ctr = 0;

    for (const key of headerSelector) {
      if (ctr > 0) result += columnDelimiter;
      // Navigate through nested object using key path
      let value = item;
      for (const keyPart of key.split('.')) {
        value = value[keyPart];
      }
      result += value;
      ctr++;
    }
    result += lineDelimiter;
  }

  return result;
};

/**
 * Handles API response data
 * @param {object} res - The API response object
 * @returns {unknown} The extracted response data or the entire data object
 */
export const ApiResponseHandler = (res) => {
  const status = res.data?.status || res.data?.Status;
  const response = res.data?.response || res.data?.Response;
  if (status && response) {
    if (status === 500) {
      throw new Error('Error');
    }
    return response;
  }
  return res.data;
};

/**
 * Adds IDs to table rows if not present
 * @param {Array|object} res - The data to process
 * @returns {unknown} The data with IDs added if applicable
 */
export const DataGridTableId = (res) => {
  if (Array.isArray(res) && res.length > 0) {
    if (res[0].id) {
      return res;
    }
    return res.map((item, index) => ({
      ...item,
      id: index,
    }));
  }
  return res;
};

/**
 * Creates a debounced version of a function
 * @param {(...args: unknown[]) => unknown} func - The function to debounce
 * @param {number} wait - The debounce wait time in milliseconds
 * @returns {(...args: unknown[]) => unknown} The debounced function
 */
export const debounce = (func, wait) => {
  let timeout;

  return (...args) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
};

/**
 * Formats a date to a specified format
 * @param {Date} date - The date to format
 * @param {string} format - The format to use ('date', 'time', or 'dateTime')
 * @returns {string | null | undefined} The formatted date string, null if no date provided, or undefined if format unrecognized
 */
export const formatDate = (date, format) => {
  let newDate;
  let dateOptions;

  if (date) {
    switch (format) {
      case 'date':
        dateOptions = {
          year: 'numeric',
          day: 'numeric',
          month: 'numeric',
          timeZone: 'CST',
        };
        // @ts-ignore
        newDate = new Date(date).toLocaleDateString('en-US', dateOptions);

        break;

      case 'time':
        dateOptions = {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: 'CST',
        };
        // @ts-ignore
        newDate = new Date(date).toLocaleTimeString('en-US', dateOptions);

        break;

      case 'dateTime':
        dateOptions = {
          year: 'numeric',
          day: 'numeric',
          month: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'CST',
        };
        // @ts-ignore
        newDate = new Date(date).toLocaleTimeString('en-US', dateOptions);

        break;

      // No default
    }
  } else {
    newDate = null;
  }
  return newDate;
};

/**
 * Moves an element in an array from one position to another
 * @param {Array} arr - The array to modify
 * @param {number} fromIndex - The index to move from
 * @param {number} toIndex - The index to move to
 * @returns {void}
 */
export const arrayMove = (arr, fromIndex, toIndex) => {
  if (!fromIndex || !toIndex) {
    return;
  }
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
};
